import { CalculationResults } from '@/types/businesscase';
import { cn } from '@/lib/utils';
import { CheckCircle, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { beoordeelBusinesscase, OordeelNiveau } from '@/lib/businesscaseOordeel';

/**
 * De risicokaart rekent zelf niets meer uit. Zij rendert het ene oordeel uit
 * `beoordeelBusinesscase` (./oordeel), net als BusinesscaseSummary — zo kunnen
 * er geen twee oordelen meer naast elkaar op de pagina staan.
 *
 * De drempels zijn ongewijzigd overgenomen uit de oude `assessRisk` die hier
 * stond (ROI < 5, terugverdientijd > 10, kasstroom < 0 voor "Hoog Risico";
 * ROI < 10 of terugverdientijd > 7 voor "Matig Risico"), zodat de zeventien
 * consistente cases hetzelfde label houden.
 */

interface RiskIndicatorProps {
  calculations: CalculationResults;
  className?: string;
}

const riskConfig: Record<OordeelNiveau, {
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  Icon: typeof CheckCircle;
}> = {
  goed: {
    bgColor: 'bg-green-100 dark:bg-green-900/50',
    borderColor: 'border-green-300 dark:border-green-700',
    textColor: 'text-green-800 dark:text-green-200',
    iconColor: 'text-green-600 dark:text-green-400',
    Icon: CheckCircle,
  },
  aandacht: {
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/50',
    borderColor: 'border-yellow-300 dark:border-yellow-700',
    textColor: 'text-yellow-800 dark:text-yellow-200',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    Icon: AlertTriangle,
  },
  slecht: {
    bgColor: 'bg-red-100 dark:bg-red-900/50',
    borderColor: 'border-red-300 dark:border-red-700',
    textColor: 'text-red-800 dark:text-red-200',
    iconColor: 'text-red-600 dark:text-red-400',
    Icon: XCircle,
  },
  // Geen getallen is geen oordeel — dat hoort zichtbaar te zijn, niet weggerond
  // naar groen of rood.
  onbekend: {
    bgColor: 'bg-muted',
    borderColor: 'border-border',
    textColor: 'text-muted-foreground',
    iconColor: 'text-muted-foreground',
    Icon: HelpCircle,
  },
};

export function RiskIndicator({ calculations, className }: RiskIndicatorProps) {
  const oordeel = beoordeelBusinesscase({
    roiYear1: calculations.roiYear1,
    totalCashflow: calculations.totalCashflow,
    paybackPeriod: calculations.paybackPeriod,
    roiMode: calculations.roiMode,
    looptijdJaren: calculations.yearlyData?.length,
  });
  const config = riskConfig[oordeel.niveau];
  const { Icon } = config;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-help transition-colors',
              config.bgColor,
              config.borderColor,
              className
            )}
          >
            <Icon className={cn('h-5 w-5', config.iconColor)} />
            <span className={cn('font-medium text-sm', config.textColor)}>
              {oordeel.risicoLabel}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <p className="font-semibold mb-1">{oordeel.kop}</p>
          <p className="text-sm">{oordeel.uitleg}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
